import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import logo from "../../components/Assets/logo.png";
import { API_BASE_URL } from "../../Config/config";

// Static projection component
const StaticProjection = ({ width = 200, top = "0%", showBorder = false }) => {
  return (
    <div
      className="absolute  h-[90px] opacity-80 rounded-full"
      style={{
        width: `${width}px`,
        transform: `translateX(-${width / 2}px) rotate(18deg)`,
        top,
        left: 0,
        backgroundColor: "#0041C2",
        border: showBorder ? "8px solid #87ceeb" : "none",
      }}
    />
  );
};

// Blue projections container
const BlueProjections = () => {
  const projections = [
    { width: 650, top: "0%", showBorder: false },
    { width: 950, top: "25%", showBorder: true },
    { width: 300, top: "50%", showBorder: true },
    { width: 520, top: "75%", showBorder: false },
  ];

  return (
    <div className="absolute top-0 left-0 h-full w-full overflow-hidden z-0 pointer-events-none">
      {projections.map(({ width, top, showBorder }, i) => (
        <StaticProjection
          key={i}
          width={width}
          top={top}
          showBorder={showBorder}
        />
      ))}
    </div>
  );
};

const UserLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password,
      });

      // authentication token
      const token = response.data.access_token;

      //
      localStorage.setItem("authToken", token);

      window.location.href = "/homepage";
    } catch (error) {
      setErrorMessage("Invalid email or password");
      //console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-white relative overflow-hidden">
      <ToastContainer />

      {/* Static Blue Projections */}
      <BlueProjections />

      <div className="min-h-screen flex flex-col items-center justify-center px-8 relative z-10 space-y-6">
        <h1 className="text-4xl flex text-center font-sans mb-0 font-semibold">
          <span className="text-black">IRIDIS</span>
          <span className="text-[#0041C2] ml-2"> AMS</span>
        </h1>

        {/* Centered Login Container  */}
        <div className="w-full max-w-2xl h-auto rounded-2xl bg-white border border-gray-300 px-16 py-12 relative">
          {/* Login Form */}
          <div className="max-w-xs mt-10 mb-10 mx-auto">
            {/* Welcome Message */}
            <div className="mb-6">
              <p className="text-gray-800 text-2xl font-medium">
                Welcome Back!
              </p>
              <p className="text-gray-600 text-sm">Please enter your details</p>
            </div>

            <form className="space-y-4" onSubmit={handleLogin}>
              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="text"
                  placeholder="Enter Username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-300 focus:outline-none text-sm"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Password Input */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1.5"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring focus:ring-blue-300 focus:outline-none text-sm"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 disabled:opacity-50"
                    disabled={isLoading}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Remember Me and Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-700"
                  >
                    Remember me
                  </label>
                </div>
                <Link
                  to="/forgotpassword"
                  className="text-sm text-blue-500 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className={`w-full py-2 rounded-lg transition duration-200 ${
                  isLoading
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                } text-white font-medium text-sm`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Logging in...
                  </div>
                ) : (
                  "Login"
                )}
              </button>
            </form>

            {errorMessage && (
              <p className="text-red-500 text-sm mt-4 text-center">
                {errorMessage}
              </p>
            )}
          </div>

          {/* Logo with label */}
          <div className="absolute bottom-2 left-4 flex items-center space-x-3">
            <img src={logo} alt="Logo" className="w-16 h-auto" />
            <div className="text-sm text-gray-500 font-light leading-snug">
              Asset Management System
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;
